import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Icon from "../../../../components/Icon";
import Button from "../../../../components/Button";
import api from "../../../../services/api";
import { renderError } from "../../../../helpers/errors";
import { CORES_TINTA, MATERIAIS, SEM_TINTA, tecnicaPor } from "../ModalGenerateMockup/opcoes";
import "./styles.scss";

const logoSrc = (imageUrl) =>
    typeof imageUrl === 'string' ? imageUrl : imageUrl?.thumbnail ?? imageUrl?.original ?? null;

const saveDataUri = (dataUri, name) => {
    const link = document.createElement('a');
    link.href = dataUri;
    link.download = /\.[a-z0-9]{2,4}$/i.test(name ?? '') ? name : `${name || 'logotipo'}.png`;
    link.click();
};

// O logotipo guardado vem pela rota de dados: a URL do asset é de outra
// origem, e o navegador ignora o `download` e só abre a imagem numa aba.
const downloadLogotype = async (logo) => {
    if (typeof logo.image_url === 'string' && logo.image_url.startsWith('data:')) {
        saveDataUri(logo.image_url, logo.name);
        return;
    }

    try {
        const res = await api.get(`/customer-logotype/${logo.id}/data`);
        saveDataUri(res.data.resource.data_uri, res.data.resource.name ?? logo.name);
    } catch (err) {
        renderError(err);
    }
};

const describeInk = (info) => {
    if (SEM_TINTA.includes(info.technique)) return { label: 'Cor do material' };
    if (!info.ink_color) return { label: 'Cores do logotipo' };

    const known = CORES_TINTA.find((item) => item.value?.toLowerCase() === info.ink_color.toLowerCase());

    return { label: known?.label ?? info.ink_color.toUpperCase(), swatch: info.ink_color };
};

const MockupDetails = ({ info, manualFileName, colorMismatch }) => {
    if (!info) {
        return (
            <p className="mockup-panel__manual">
                <Icon name="upload" size={14} />
                <span>
                    Amostra enviada como arquivo
                    {manualFileName && <em title={manualFileName}>{manualFileName}</em>}
                </span>
            </p>
        );
    }

    const ink = describeInk(info);
    const material = MATERIAIS.find((item) => item.value === info.material && item.value);
    const logo = info.logotype;

    return (
        <>
            {info.origin === 'site' && (
                <span className="mockup-panel__origin">Criada pelo cliente no site</span>
            )}
            <dl className="mockup-panel__details">
                {logo && (
                    <div className="mockup-panel__detail mockup-panel__detail--logo">
                        <dt>Logotipo</dt>
                        <dd>
                            {logoSrc(logo.image_url) && <img src={logoSrc(logo.image_url)} alt="" />}
                            <span title={logo.name}>{logo.name || 'Logotipo do cliente'}</span>
                            {(logo.id || String(logo.image_url ?? '').startsWith('data:')) && (
                                <button
                                    type="button"
                                    className="mockup-panel__logo-download"
                                    onClick={() => downloadLogotype(logo)}
                                    aria-label="Baixar logotipo"
                                    title="Baixar logotipo"
                                >
                                    <Icon name="download" size={14} />
                                </button>
                            )}
                        </dd>
                    </div>
                )}
                <div className="mockup-panel__detail">
                    <dt>Gravação</dt>
                    <dd>{tecnicaPor(info.technique)?.label ?? info.technique ?? 'Não informada'}</dd>
                </div>
                <div className="mockup-panel__detail">
                    <dt>Cor da gravação</dt>
                    <dd>
                        {ink.swatch && <i className="mockup-panel__swatch" style={{ background: ink.swatch }} />}
                        {ink.label}
                    </dd>
                </div>
                {material && (
                    <div className="mockup-panel__detail">
                        <dt>Material</dt>
                        <dd>{material.label}</dd>
                    </div>
                )}
            </dl>
            {colorMismatch && (
                <p className="mockup-panel__warning">
                    <Icon name="alert" size={14} />
                    A amostra foi feita em outra cor do produto. Gere de novo para acompanhar a cor atual.
                </p>
            )}
        </>
    );
};

const Lightbox = ({ views, initial, close }) => {
    const [current, setCurrent] = useState(initial);
    const view = views.find((item) => item.key === current) ?? views[0];

    useEffect(() => {
        const onKey = (event) => {
            if (event.key === 'Escape') close();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [close]);

    return createPortal(
        <div className="mockup-lightbox" onClick={close} role="dialog" aria-modal="true">
            <button type="button" className="mockup-lightbox__close" aria-label="Fechar" onClick={close}>
                <Icon name="close" size={22} />
            </button>
            {views.length > 1 && (
                <div className="mockup-lightbox__tabs" onClick={(event) => event.stopPropagation()}>
                    {views.map((item) => (
                        <button
                            key={item.key}
                            type="button"
                            className={item.key === view.key ? 'is-active' : ''}
                            onClick={() => setCurrent(item.key)}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
            )}
            <img src={view.src} alt={view.label} onClick={(event) => event.stopPropagation()} />
        </div>,
        document.body
    );
};

export const MockupPanel = ({
    hasProduct,
    productImageSrc,
    productImageLoading,
    mockupSrc,
    mockupIsPdf,
    mockupInfo,
    manualFileName,
    colorMismatch,
    saving,
    savingText,
    canEdit,
    canGenerate,
    canPersist,
    canDownloadPdf,
    dropzone,
    onGenerate,
    onRemove,
    onDownloadPdf
}) => {
    const [lightbox, setLightbox] = useState(null);
    const hasMockup = Boolean(mockupSrc);
    const hasImageMockup = hasMockup && !mockupIsPdf;
    const stageSrc = hasImageMockup ? mockupSrc : productImageSrc;

    const views = [
        hasImageMockup && { key: 'mockup', label: 'Amostra virtual', src: mockupSrc },
        productImageSrc && { key: 'product', label: 'Foto do produto', src: productImageSrc }
    ].filter(Boolean);

    const { getRootProps, getInputProps, isDragActive, open } = dropzone;

    return (
        <aside
            {...getRootProps({
                className: `modal-product-sidebar mockup-panel${isDragActive ? ' mockup-panel--drop-active' : ''}`
            })}
        >
            <input {...getInputProps()} />

            <div className="mockup-panel__header">
                <h3>Amostra virtual</h3>
                <span className={`mockup-panel__status${hasMockup ? ' is-ready' : ''}`}>
                    {hasMockup ? 'Pronta' : 'Não criada'}
                </span>
            </div>

            <div className="mockup-panel__stage">
                {saving || productImageLoading ? (
                    <Icon name="loading" className="mockup-panel__loading spin" />
                ) : hasMockup && mockupIsPdf ? (
                    <button type="button" className="mockup-panel__pdf" onClick={onDownloadPdf}>
                        <span>PDF</span>
                        Abrir amostra enviada
                    </button>
                ) : stageSrc ? (
                    <button
                        type="button"
                        className="mockup-panel__image"
                        onClick={() => setLightbox(hasImageMockup ? 'mockup' : 'product')}
                        aria-label="Ampliar imagem"
                    >
                        <img key={stageSrc} src={stageSrc} alt="" />
                        <span className="mockup-panel__zoom">
                            <Icon name="zoom-in" size={14} />
                            Ampliar
                        </span>
                    </button>
                ) : (
                    <p className="mockup-panel__empty">
                        {hasProduct ? 'Produto sem foto cadastrada.' : 'Selecione o produto para criar a amostra.'}
                    </p>
                )}
                {!hasMockup && stageSrc && !saving && (
                    <span className="mockup-panel__stage-tag">Foto do produto</span>
                )}
                {isDragActive && <div className="mockup-panel__drop-hint">Solte para usar esta arte</div>}
            </div>

            {saving && savingText && <p className="mockup-panel__hint">{savingText}</p>}

            {!hasMockup && hasProduct && canEdit && (
                <div className="mockup-panel__cta">
                    <div className="mockup-panel__cta-icon">
                        <Icon name="sparkles" size={20} />
                    </div>
                    <strong>Mostre o logo do cliente no produto</strong>
                    <p>Marque a área, escolha a gravação e a amostra virtual fica pronta na hora.</p>
                    <Button
                        type="primary"
                        svg="sparkles"
                        text="Gerar com IA"
                        action={onGenerate}
                        disabled={!canGenerate}
                        btnClass="mockup-panel__cta-button"
                    />
                    <button type="button" className="mockup-panel__link" onClick={open}>
                        ou envie uma arte pronta
                    </button>
                    <small>Arraste o arquivo aqui · recomendado 1.000 × 1.000 px</small>
                </div>
            )}

            {!hasMockup && hasProduct && !canEdit && !saving && (
                <p className="mockup-panel__hint">Nenhuma amostra virtual enviada.</p>
            )}

            {hasMockup && !saving && (
                <div className="mockup-panel__info">
                    <MockupDetails info={mockupInfo} manualFileName={manualFileName} colorMismatch={colorMismatch} />

                    <div className="mockup-panel__actions">
                        <Button
                            type="secondary"
                            svg="pdf"
                            text="Baixar amostra virtual"
                            action={onDownloadPdf}
                            disabled={!canDownloadPdf}
                            btnClass="mockup-panel__action-main"
                        />
                        {!canDownloadPdf && (
                            <p className="mockup-panel__hint">O PDF fica disponível depois de salvar o item.</p>
                        )}
                        {canEdit && (
                            <div className="mockup-panel__secondary-actions">
                                <button type="button" onClick={onGenerate} disabled={!canGenerate}>
                                    <Icon name="sparkles" size={14} />
                                    Gerar de novo
                                </button>
                                <button type="button" onClick={open}>
                                    <Icon name="upload" size={14} />
                                    Trocar arquivo
                                </button>
                                <button type="button" className="is-danger" onClick={onRemove}>
                                    <Icon name="delete" size={14} />
                                    Remover
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {!canPersist && (
                <p className="mockup-panel__hint mockup-panel__hint--footer">
                    Associe um cliente ao orçamento para gravar a amostra virtual e recuperar as já salvas.
                </p>
            )}

            {lightbox && views.length > 0 && (
                <Lightbox views={views} initial={lightbox} close={() => setLightbox(null)} />
            )}
        </aside>
    );
};

export default MockupPanel;
